import { FC } from 'react';
import { FundStatus } from 'further-types/fund';
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Cached } from '@mui/icons-material';
import { Row } from 'further-ui/components/Layout';
import Dot from 'further-ui/components/Dot';
import { TrancheDotColor } from './dotColors';

const dotColorByStatus: Record<FundStatus, string> = {
  [FundStatus.Draft]: TrancheDotColor.Draft,
  [FundStatus.Open]: TrancheDotColor.Open,
  [FundStatus.Closed]: TrancheDotColor.Closed,
};

const labelByStatus: Record<FundStatus, string> = {
  [FundStatus.Draft]: 'Draft',
  [FundStatus.Open]: 'Open',
  [FundStatus.Closed]: 'Closed',
};

const allStatuses = [FundStatus.Draft, FundStatus.Open, FundStatus.Closed];

const SelectWrapper = styled(Box)({
  width: 220,
});

const OptionRow = styled(Row)(() => ({
  width: '100%',
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.grey[900],
}));

const StyledCached = styled(Cached)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

type Props = {
  value: FundStatus;
  onChange: (value: FundStatus) => void;
  isSaving?: boolean;
  disabled?: boolean;
  hiddenStatuses?: Array<FundStatus>;
};

const StatusSelect: FC<Props> = ({
  value,
  onChange,
  isSaving,
  disabled,
  hiddenStatuses,
}) => {
  const handleChange = (event: SelectChangeEvent<FundStatus>) => {
    onChange(event.target.value);
  };

  const hasOptions = allStatuses.some(
    (status) => status !== value && !hiddenStatuses?.includes(status),
  );

  return (
    <SelectWrapper>
      <Select<FundStatus>
        fullWidth
        value={value}
        onChange={handleChange}
        disabled={disabled || isSaving || !hasOptions}
        renderValue={(selected) => (
          <Row spacing="xs" centered>
            <Typography variant="inherit">Tranche:</Typography>
            {isSaving ? (
              <>
                <StyledCircularProgress size={14} />
                <Typography variant="inherit">Saving</Typography>
              </>
            ) : (
              <>
                <Dot size={6} color={dotColorByStatus[selected]} />
                <Typography variant="inherit">
                  {labelByStatus[selected]}
                </Typography>
              </>
            )}
          </Row>
        )}
      >
        {allStatuses
          .filter(
            (status) => status !== value && !hiddenStatuses?.includes(status),
          )
          .map((status) => (
            <MenuItem key={status} value={status}>
              <OptionRow spacing="sm" centered justify="space-between">
                <Row spacing="sm" centered>
                  <Dot size={6} color={dotColorByStatus[status]} />
                  <Typography variant="inherit">
                    Switch to {labelByStatus[status]}
                  </Typography>
                </Row>
                <StyledCached fontSize="small" />
              </OptionRow>
            </MenuItem>
          ))}
      </Select>
    </SelectWrapper>
  );
};

export default StatusSelect;
